import { useState } from 'react';

import ChatList from '../components/Chat/ChatList';
import ChatWindow from '../components/Chat/ChatWindow';
import ToolsSidebar from '../components/Chat/ToolsSidebar';
import LeftSidebar, { type LeftView } from '../components/Chat/LeftSidebar';
import TemplateManager from '../components/Chat/TemplateManager';
import AdsManager from '../components/Chat/AdsManager';
import { useChat } from '../hooks/useChat';
import type { ConversationType } from '../types';
import { conversationTypeLabel, getUnsupportedReason } from '../utils/conversation-capabilities';

const UnavailablePanel = ({ title, message }: { title: string; message: string }) => (
  <section className="left-view-container unavailable-view" aria-label={title}>
    <div className="unavailable-illustration" aria-hidden="true">🚧</div>
    <h2>{title}</h2>
    <p>{message}</p>
  </section>
);

const CatalogPanel = () => (
  <section className="left-view-container unavailable-view" aria-label="Catalogus">
    <div className="unavailable-illustration" aria-hidden="true">🛍️</div>
    <h2>Catalogus</h2>
    <p>Nog te bouwen — technisch mogelijk via de WhatsApp Business API.</p>
  </section>
);

const VIEW_TO_CONVERSATION_TYPE: Record<LeftView, ConversationType | undefined> = {
  chats: 'direct',
  groups: 'group',
  communities: 'community',
  channels: 'channel',
  status: 'status',
  ads: undefined,
  catalog: undefined,
  templates: undefined,
};

const Home = () => {
  const [leftView, setLeftView] = useState<LeftView>('chats');
  const { accounts } = useChat();

  const activeAccount = accounts.find((a) => a.isActive) ?? accounts[0];
  const conversationType = VIEW_TO_CONVERSATION_TYPE[leftView];

  const renderChatView = () => {
    if (!conversationType) return null;

    const isSupported = activeAccount
      ? activeAccount.capabilities.some(
          (capability) => capability.type === conversationType && capability.supported,
        )
      : false;

    if (!isSupported) {
      const reason = getUnsupportedReason(activeAccount, conversationType) ?? 'Niet ondersteund door het actieve account.';
      const title = conversationType === 'direct' ? 'Chats' : conversationTypeLabel(conversationType);
      return (
        <UnavailablePanel
          title={title}
          message={reason}
        />
      );
    }

    return (
      <>
        <ChatList key={leftView} conversationType={conversationType} />
        <ChatWindow />
      </>
    );
  };

  return (
    <main className="chat-layout">
      <LeftSidebar activeView={leftView} onViewChange={setLeftView} />

      {leftView === 'chats' && renderChatView()}
      {leftView === 'groups' && renderChatView()}
      {leftView === 'communities' && renderChatView()}
      {leftView === 'channels' && renderChatView()}
      {leftView === 'status' && renderChatView()}


      {leftView === 'ads' && (
        <div className="left-view-container">
          <AdsManager />
        </div>
      )}
      {leftView === 'catalog' && <CatalogPanel />}
      {leftView === 'templates' && (
        <div className="left-view-container">
          <TemplateManager />
        </div>
      )}

      {(leftView === 'chats' || leftView === 'groups') && <ToolsSidebar />}
    </main>
  );
};

export default Home;
